// src/utils/timezone.js

// Parse database datetime string (which is stored in Nairobi time)
// Format: "2026-06-12 15:43:24"
export const parseNairobiTime = (dateString) => {
  if (!dateString) return new Date();
  
  try {
    // If it's already a Date object, return it
    if (dateString instanceof Date) return dateString;
    
    // Handle "2026-06-12 15:43:24" format (Nairobi time in database)
    if (typeof dateString === 'string' && dateString.includes(' ') && !dateString.includes('T')) {
      // Split into date and time parts
      const [datePart, timePart] = dateString.split(' ');
      const [year, month, day] = datePart.split('-');
      const [hour, minute, second] = timePart.split(':');
      
      // Create date directly (no timezone conversion)
      // This keeps the time exactly as stored in database
      const date = new Date(year, month - 1, day, hour, minute, second || 0);
      return date;
    }
    
    // Handle standard ISO format
    return new Date(dateString);
  } catch (e) {
    console.error('Date parsing error:', e);
    return new Date();
  }
};

// Format date for display (no conversion - show as is)
export const formatDateTime = (dateString, format = 'full') => {
  if (!dateString) return 'N/A';
  
  try {
    // Parse the Nairobi database time
    const date = parseNairobiTime(dateString);
    
    switch (format) {
      case 'time':
        return date.toLocaleTimeString('en-KE', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      
      case 'date':
        return date.toLocaleDateString('en-KE', {
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        });
      
      case 'datetime':
        return date.toLocaleString('en-KE', {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: false
        });
      
      default:
        return date.toLocaleString('en-KE');
    }
  } catch (e) {
    console.error('Date formatting error:', e);
    return dateString;
  }
};

// Calculate remaining time from expiry date
export const getRemainingTime = (expiresAtString) => {
  if (!expiresAtString) return 'No expiry';
  
  try {
    // Parse the expiry time (database Nairobi time)
    const expiryDate = parseNairobiTime(expiresAtString);
    
    // Get current Nairobi time
    const now = new Date();
    const nowNairobi = new Date(now.getTime() + (3 * 60 * 60 * 1000));
    
    // Calculate difference in milliseconds
    const diffMs = expiryDate - nowNairobi;
    
    if (diffMs <= 0) return 'Expired';
    
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} remaining`;
    }
    if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} remaining`;
    }
    return `${diffDays} day${diffDays !== 1 ? 's' : ''} remaining`;
  } catch (e) {
    console.error('Time calculation error:', e);
    return 'Unknown';
  }
};

// Format how long ago a message was created
export const formatTimeAgo = (createdAtString) => {
  if (!createdAtString) return 'Just now';
  
  try {
    const createdDate = parseNairobiTime(createdAtString);
    
    // Get current Nairobi time
    const now = new Date();
    const nowNairobi = new Date(now.getTime() + (3 * 60 * 60 * 1000));
    
    const diffMs = nowNairobi - createdDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} min ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  } catch (e) {
    return 'Just now';
  }
};

// Get current time for display
export const getCurrentTime = () => {
  const now = new Date();
  const nairobiTime = new Date(now.getTime() + (3 * 60 * 60 * 1000));
  return nairobiTime.toLocaleTimeString('en-KE', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  });
};

// Get current Nairobi date for debugging
export const getCurrentNairobiTime = () => {
  const now = new Date();
  return new Date(now.getTime() + (3 * 60 * 60 * 1000));
};